// FTEDN Team - Voting & Ideas Management
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import './VotingMgmt.css';

interface Idea {
  id: string;
  title?: string;
  status?: string;
  userName?: string;
}

interface VotingCard {
  id: string;
  title?: string;
  totalVotes?: number;
  likes?: number;
  status?: string;
}

function getItems<T>(response: unknown): T[] {
  const payload = response as { data?: T[] };
  return Array.isArray(payload?.data) ? payload.data : [];
}

export function VotingMgmtPage() {
  const { get, patch } = useApi();
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [cards, setCards] = useState<VotingCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);

  const loadVoting = useCallback(async () => {
    setError('');
    try {
      const [ideasResponse, resultsResponse] = await Promise.all([
        get('/voting/ideas'),
        get('/voting/results'),
      ]);
      setIdeas(getItems<Idea>(ideasResponse));
      setCards(getItems<VotingCard>(resultsResponse));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load voting operations');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    void loadVoting();
  }, [loadVoting]);

  async function updateIdea(idea: Idea, status: 'approved' | 'rejected') {
    setActingId(idea.id);
    setError('');
    try {
      await patch(`/voting/ideas/${idea.id}/status`, { status });
      await loadVoting();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update idea');
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="mgmt-page">
      <div className="mgmt-header">
        <h1>Voting & Ideas</h1>
        <p className="mgmt-subtitle">Manage voting cards and review creator ideas</p>
        <div className="mgmt-stats">
          <span className="stat-chip">{ideas.length} Ideas</span>
          <span className="stat-chip approved">{cards.length} Cards</span>
        </div>
      </div>
      {error && <div className="mgmt-error" role="alert">{error}</div>}
      {loading && <div className="mgmt-empty">Loading voting operations...</div>}
      {!loading && (
        <>
          <h2 className="section-title">Idea Submissions</h2>
          <div className="ideas-list">
            {ideas.length === 0 && <div className="mgmt-empty">No idea submissions yet.</div>}
            {ideas.map((idea) => {
              const title = idea.title || 'Untitled idea';
              const status = idea.status || 'submitted';
              return (
                <div key={idea.id} className="idea-row">
                  <div className="idea-row-info">
                    <strong>{title}</strong>
                    <span>{idea.userName || 'Creator'} submitted this idea</span>
                  </div>
                  <span className={`status-badge ${status}`}>{status}</span>
                  {status === 'submitted' && (
                    <div className="mgmt-actions">
                      <button className="action-btn approve" disabled={actingId === idea.id} onClick={() => void updateIdea(idea, 'approved')} aria-label={`Approve ${title}`}>Approve</button>
                      <button className="action-btn reject" disabled={actingId === idea.id} onClick={() => void updateIdea(idea, 'rejected')} aria-label={`Reject ${title}`}>Reject</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <h2 className="section-title">Voting Results</h2>
          <div className="ideas-list">
            {cards.length === 0 && <div className="mgmt-empty">No voting cards yet.</div>}
            {cards.map((card) => (
              <div key={card.id} className="idea-row">
                <div className="idea-row-info">
                  <strong>{card.title || 'Voting card'}</strong>
                  <span>{card.totalVotes ?? 0} votes - {card.likes ?? 0} likes</span>
                </div>
                <span className={`status-badge ${card.status || 'active'}`}>{card.status || 'active'}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
